import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/auth/AuthLayout';
import FormInput from '../components/auth/FormInput';
import PasswordStrength from '../components/auth/PasswordStrength';
import SocialButtons from '../components/auth/SocialButtons';
import { useGoogleAuth } from '../hooks/useGoogleAuth';

export default function RegisterPage() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  // GDPR: product news is opt-in — this box is never pre-ticked
  const [productUpdates, setProductUpdates] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const { register, error, clearError } = useAuth();
  const navigate = useNavigate();

  // Google Identity Services - Popup mode
  const { signIn: googleSignIn, isLoading: googleLoading } = useGoogleAuth({
    onSuccess: (data) => {
      console.log('✅ Google signup success:', data);
      // Store tokens
      localStorage.setItem('pilotleague_token', data.token);
      localStorage.setItem('pilotleague_refresh_token', data.refreshToken);
      // Redirect to dashboard
      window.location.href = '/v4';
    },
    onError: (error) => {
      console.error('❌ Google signup error:', error);
      setFormError(error.message || t('auth.register.errors.googleError'));
    },
  });

  const validateForm = () => {
    if (!username || !email || !password) {
      setFormError(t('auth.register.errors.fillAllFields'));
      return false;
    }

    if (username.length < 3 || username.length > 50) {
      setFormError(t('auth.register.errors.usernameLength'));
      return false;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      setFormError(t('auth.register.errors.usernameFormat'));
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError(t('auth.register.errors.invalidEmail'));
      return false;
    }

    if (password.length < 8) {
      setFormError(t('auth.register.errors.passwordLength'));
      return false;
    }

    if (!acceptTerms) {
      setFormError(t('auth.register.errors.acceptTerms'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await register({ username, email, password, acceptTerms, productUpdates });
      navigate('/success', {
        state: {
          type: 'registration',
          message: t('success.registration.message'),
        },
      });
    } catch (err) {
      if (err.status === 409) {
        setFormError(t('auth.register.errors.accountExists'));
      } else {
        setFormError(err.message || t('auth.register.errors.registrationError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignup = () => {
    setFormError('');
    googleSignIn();
  };

  const features = [
    {
      icon: '✈️',
      title: t('auth.features.register.free.title'),
      description: t('auth.features.register.free.description'),
    },
    {
      icon: '🔒',
      title: t('auth.features.register.secure.title'),
      description: t('auth.features.register.secure.description'),
    },
    {
      icon: '⚡',
      title: t('auth.features.register.fast.title'),
      description: t('auth.features.register.fast.description'),
    },
  ];

  return (
    <AuthLayout tagline={t('auth.taglines.register')} features={features}>
      <div className="auth-form-header">
        <h1 className="auth-form-title">{t('auth.register.title')}</h1>
        <p className="auth-form-subtitle">{t('auth.register.subtitle')}</p>
      </div>

      {(formError || error) && (
        <div className="form-alert form-alert-error">
          <svg viewBox="0 0 24 24" className="form-alert-icon">
            <path
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
              fill="currentColor"
            />
          </svg>
          <span>{formError || error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <FormInput
          label={t('auth.register.username')}
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t('auth.register.usernamePlaceholder')}
          icon="star"
          required
          autoComplete="username"
          hint={t('auth.register.usernameHint')}
        />

        <FormInput
          label={t('auth.register.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.register.emailPlaceholder')}
          icon="email"
          required
          autoComplete="email"
        />

        <div className="form-group">
          <FormInput
            label={t('auth.register.password')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.register.passwordPlaceholder')}
            icon="lock"
            required
            autoComplete="new-password"
          />
          <PasswordStrength password={password} />
        </div>

        <div className="form-group">
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
              required
            />
            <span className="checkbox-custom">
              <svg viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
              </svg>
            </span>
            {t('auth.register.acceptTerms')}{' '}
            <Link
              to={t('links.termsOfService')}
              className="form-link"
              style={{ marginLeft: '4px' }}
            >
              {t('auth.register.termsOfService')}
            </Link>{' '}
            {t('auth.register.andThe')}{' '}
            <Link to={t('links.privacyPolicy')} className="form-link">
              {t('auth.register.privacyPolicy')}
            </Link>
          </label>
        </div>

        <div className="form-group">
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={productUpdates}
              onChange={(e) => setProductUpdates(e.target.checked)}
              data-testid="register-product-updates"
            />
            <span className="checkbox-custom">
              <svg viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" />
              </svg>
            </span>
            {t('auth.register.productUpdatesOptIn')}
          </label>
        </div>

        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="btn-spinner" />
              {t('auth.register.submitting')}
            </>
          ) : (
            t('auth.register.submit')
          )}
        </button>
      </form>

      <div className="form-divider">
        <span>{t('auth.register.orSignUpWith')}</span>
      </div>

      <SocialButtons onGoogleClick={handleGoogleSignup} />

      <div className="auth-footer">
        {t('auth.register.alreadyHaveAccount')} <Link to="/login">{t('auth.register.signIn')}</Link>
      </div>
    </AuthLayout>
  );
}
